import type {
  CommentSubmitInput,
  FlagComplianceIssueInput,
} from "@/components/license-details/cards/comments-card";
import { toast } from "@/components/ui/sonner";
import { UserType } from "@/constants";
import {
  createLicenseCommentMutation,
  deleteLicenseCommentMutation,
} from "@/hera-api/client/@tanstack/react-query.gen";
import {
  clearComplianceIssue,
  createRegistrantLicenseComment,
  flagComplianceIssue,
  resolveRegistryNotification,
  uploadAnswerFile,
  uploadSharedAnswerFile,
} from "@/hera-api/client/sdk.gen";
import { refreshLicensesAfterLocalChange } from "@/lib/license-details-change-notification";
import { getQueryErrorMessage } from "@/lib/utils";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

interface UseCommentHandlersOptions {
  licenseId: string;
  userType?: UserType;
}

type AttachmentPayload = {
  token: string;
  filename: string;
  content_type: string;
};

async function uploadCommentFile(
  file: File,
  registrant: boolean,
): Promise<AttachmentPayload> {
  // The generated upload sends multipart form data and the bearer token.
  // A raw client.post JSON-encodes the body, so the API never returns a token.
  const resp = registrant
    ? await uploadAnswerFile({ body: { file } })
    : await uploadSharedAnswerFile({ body: { file } });
  const token = resp.data?.token;
  if (typeof token !== "string" || !token) {
    if (resp.error) throw resp.error;
    throw new Error("Upload failed: no token returned");
  }
  return {
    token,
    filename: file.name,
    content_type: file.type || "application/octet-stream",
  };
}

export function useCommentHandlers({
  licenseId,
  userType,
}: UseCommentHandlersOptions) {
  const queryClient = useQueryClient();
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [commentToDelete, setCommentToDelete] = useState<string | null>(null);
  const isRegistrant = userType === UserType.REGISTRANT;

  const refreshComments = async () => {
    await refreshLicensesAfterLocalChange(queryClient, licenseId);
    await queryClient.invalidateQueries({
      predicate: (query) => {
        const head = query.queryKey[0] as { _id?: string } | string | undefined;
        if (typeof head === "string") {
          return (
            head === "omt-ams-license-comments" ||
            head === "omt-ams-licenses" ||
            head === "omt-ams-stats"
          );
        }
        return (
          head?._id === "getLicenseComments" ||
          head?._id === "getNonRegistrantLicenseComments"
        );
      },
    });
  };

  const createCommentMutation = useMutation({
    ...createLicenseCommentMutation(),
  });

  const deleteCommentMutation = useMutation({
    ...deleteLicenseCommentMutation(),
  });

  const handleAddComment = async (
    comment: string,
    isPublic = false,
    files: File[] = [],
  ) => {
    try {
      const attachments = await uploadFiles(files);
      await createCommentMutation.mutateAsync({
        path: {
          license_id: licenseId,
        },
        body: {
          comment,
          is_public: isPublic,
          attachments,
        },
      });
      await refreshComments();
      if (isPublic) {
        await queryClient.invalidateQueries({
          queryKey: ["listLicenseNotificationEmails", licenseId],
        });
      }
    } catch (error) {
      toast({
        title: "Error",
        description: `Failed to add comment: ${getQueryErrorMessage(error)}`,
        variant: "destructive",
      });
      // Need to rethrow so the comment text area is not cleared
      throw error;
    }
  };

  const uploadFiles = async (files: File[]) =>
    Promise.all(files.map((file) => uploadCommentFile(file, isRegistrant)));

  const handleRegistrantComment = async (input: CommentSubmitInput) => {
    try {
      const attachments = await uploadFiles(input.files);
      await createRegistrantLicenseComment({
        throwOnError: true,
        path: { license_id: licenseId },
        body: {
          comment: input.comment,
          notify_registry: input.notifyRegistry,
          mark_issue_resolved: input.markIssueResolved,
          attachments,
        },
      });
      await refreshComments();
    } catch (error) {
      toast({
        title: "Error",
        description: `Failed to add comment: ${getQueryErrorMessage(error)}`,
        variant: "destructive",
      });
      throw error;
    }
  };

  const handleFlagComplianceIssue = async (input: FlagComplianceIssueInput) => {
    try {
      const attachments = await uploadFiles(input.files);
      await flagComplianceIssue({
        throwOnError: true,
        path: { license_id: licenseId },
        body: { comment: input.comment, attachments },
      });
      await refreshComments();
    } catch (error) {
      toast({
        title: "Error",
        description: `Failed to flag compliance issue: ${getQueryErrorMessage(error)}`,
        variant: "destructive",
      });
      throw error;
    }
  };

  const handleClearRegistryNotification = async (commentId: string) => {
    try {
      await resolveRegistryNotification({
        throwOnError: true,
        path: { license_id: licenseId, comment_id: commentId },
      });
      await refreshComments();
    } catch (error) {
      toast({
        title: "Error",
        description: `Failed to clear notification: ${getQueryErrorMessage(error)}`,
        variant: "destructive",
      });
      throw error;
    }
  };

  const handleClearComplianceIssue = async () => {
    try {
      await clearComplianceIssue({
        throwOnError: true,
        path: { license_id: licenseId },
      });
      await refreshComments();
    } catch (error) {
      toast({
        title: "Error",
        description: `Failed to clear compliance issue: ${getQueryErrorMessage(error)}`,
        variant: "destructive",
      });
      throw error;
    }
  };

  const handleDeleteComment = (commentId: string) => {
    setCommentToDelete(commentId);
    setIsDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!commentToDelete) {
      console.error("No comment to delete");
      return;
    }
    try {
      await deleteCommentMutation.mutateAsync({
        path: {
          license_id: licenseId,
          comment_id: commentToDelete,
        },
      });
      await refreshComments();
      setIsDeleteDialogOpen(false);
      setCommentToDelete(null);
    } catch (error) {
      toast({
        title: "Error",
        description: `Failed to delete comment: ${getQueryErrorMessage(error)}`,
        variant: "destructive",
      });
    }
  };

  return {
    handleAddComment,
    handleRegistrantComment,
    handleFlagComplianceIssue,
    handleClearRegistryNotification,
    handleClearComplianceIssue,
    handleDeleteComment,
    handleConfirmDelete,
    isDeleteDialogOpen,
    setIsDeleteDialogOpen,
    commentToDelete,
    createCommentMutation,
  };
}
